import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useEffect, useId, useRef, useState } from "react";
import { skipToken, useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { listStock } from "@/api/stock";
import { KOMBI_LIMITS } from "@/api/pricing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface SelectedComponent { sku: string; merchant_sku: string | null; name: string; qty: number }
export interface SelectedKombi { key: string; components: SelectedComponent[] }
type DraftComponent = Omit<SelectedComponent, "qty"> & { qty: string };
type Slot = "Stückliste" | "A" | "B";
const validQty = (value: string) => /^\d+$/.test(value.trim()) && Number(value) >= KOMBI_LIMITS.minQty && Number(value) <= KOMBI_LIMITS.maxQty;
export const kombiLabel = (components: SelectedComponent[]) => components.map(c => `${c.merchant_sku ?? c.sku} ×${c.qty}`).join(" + ");
const signature = (components: SelectedComponent[]) => JSON.stringify(components.map(c => [c.sku, c.qty]).sort(([a], [b]) => String(a).localeCompare(String(b))));

export function KombiBuilder({ kombis, onChange }: { kombis: SelectedKombi[]; onChange: (kombis: SelectedKombi[]) => void }) {
  const [mode, setMode] = useState<"list" | "pairs">("list");
  const [drafts, setDrafts] = useState<Record<Slot, DraftComponent[]>>({ "Stückliste": [], A: [], B: [] });
  const [notice, setNotice] = useState("");
  const nextKey = useRef(1);
  const selectSlot = (slot: Slot, values: DraftComponent[]) => setDrafts(prev => ({ ...prev, [slot]: values }));
  const complete = (slot: Slot, min: number) => drafts[slot].length >= min && drafts[slot].every(c => validQty(c.qty));
  const numeric = (slot: Slot): SelectedComponent[] => drafts[slot].map(c => ({ ...c, qty: Number(c.qty) }));

  function addKombis(candidates: SelectedComponent[][], skipped = 0) {
    const seen = new Set(kombis.map(k => signature(k.components)));
    const skus = new Set(kombis.flatMap(k => k.components.map(c => c.sku)));
    const added: SelectedKombi[] = [];
    let duplicates = 0, overLimit = 0, overSkus = 0;
    for (const components of candidates) {
      const sig = signature(components);
      if (seen.has(sig)) { duplicates++; continue; }
      if (kombis.length + added.length >= KOMBI_LIMITS.maxKombis) { overLimit++; continue; }
      const combinedSkus = new Set([...skus, ...components.map(c => c.sku)]);
      if (combinedSkus.size > KOMBI_LIMITS.maxSkus) { overSkus++; continue; }
      components.forEach(c => skus.add(c.sku));
      seen.add(sig);
      added.push({ key: `k${nextKey.current++}`, components });
    }
    if (added.length) onChange([...kombis, ...added]);
    setNotice([
      added.length ? `${added.length} Kombi${added.length === 1 ? "" : "s"} hinzugefügt` : "",
      skipped ? `${skipped} Paar${skipped === 1 ? "" : "e"} mit gleicher SKU übersprungen` : "",
      duplicates ? `${duplicates} Kombi${duplicates === 1 ? "" : "s"} bereits enthalten` : "",
      overLimit ? "max. 100 Kombis — weitere nicht aufgenommen" : "",
      overSkus ? "max. 500 verschiedene Artikel je Abruf — weitere nicht aufgenommen" : "",
    ].filter(Boolean).join(" · "));
  }
  function combine() {
    if (!complete("A", 1) || !complete("B", 1)) return;
    const candidates: SelectedComponent[][] = [];
    let skipped = 0;
    for (const a of numeric("A")) for (const b of numeric("B")) {
      if (a.sku === b.sku) skipped++;
      else candidates.push([a, b]);
    }
    addKombis(candidates, skipped);
  }
  return (
    <section aria-label="Kombi (ohne Listing)" className="rounded-md border border-line bg-panel p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-semibold text-ink">Kombi (ohne Listing)</h2>
          <p className="text-xs text-ink-2 mt-1">Artikel zusammenstellen und als gemeinsame Einheit rechnen.</p>
        </div>
        <div role="group" aria-label="Kombi-Modus" className="flex gap-1 rounded-md bg-panel-2 p-1">
          {([['list', 'Stückliste'], ['pairs', 'Paare A × B']] as const).map(([value, label]) => (
            <Button key={value} size="sm" variant={mode === value ? "default" : "ghost"} aria-pressed={mode === value} onClick={() => { setMode(value); setNotice(""); }}>{label}</Button>
          ))}
        </div>
      </div>
      <div className={cn("grid gap-3", mode === "pairs" && "md:grid-cols-2")}>
        {(mode === "list" ? ["Stückliste"] as const : ["A", "B"] as const).map(slot => (
          <ArticleSlot key={slot} slot={slot} components={drafts[slot]} onChange={values => selectSlot(slot, values)} onNotice={setNotice} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {mode === "list" ? (
          <Button size="sm" disabled={!complete("Stückliste", KOMBI_LIMITS.minComponents)} onClick={() => { if (complete("Stückliste", KOMBI_LIMITS.minComponents)) addKombis([numeric("Stückliste")]); }}>Kombi hinzufügen</Button>
        ) : <Button size="sm" disabled={!complete("A", 1) || !complete("B", 1)} onClick={combine}>Kombinieren</Button>}
        <p role="status" className="text-xs text-ink-2">{notice || (mode === "list" ? "2–10 verschiedene Artikel · Menge je Artikel 1–100" : "Bis zu 10 Artikel je Slot · max. 100 Kombis")}</p>
      </div>
      {kombis.length > 0 && (
        <div className="border-t border-line pt-3 space-y-2">
          <div className="flex justify-between items-center text-xs text-ink-2"><span>{kombis.length} / 100 Kombis im Abruf</span><Button size="sm" variant="ghost" onClick={() => { onChange([]); setNotice(""); }}>Alle entfernen</Button></div>
          <ul aria-label="Kombis des Abrufs" className="flex flex-wrap gap-2 max-h-52 overflow-y-auto">
            {kombis.map(k => <li key={k.key} className="inline-flex items-center gap-2 border border-primary/30 rounded-md bg-primary-soft pl-2.5 text-xs">
              <span className="font-mono text-primary break-words">{kombiLabel(k.components)}</span>
              <Button size="icon" variant="ghost" className="size-8 shrink-0" aria-label={`Kombi ${k.key} entfernen`} onClick={() => onChange(kombis.filter(v => v.key !== k.key))}><X className="size-3" aria-hidden="true" /></Button>
            </li>)}
          </ul>
        </div>
      )}
    </section>
  );
}

function ArticleSlot({ slot, components, onChange, onNotice }: { slot: Slot; components: DraftComponent[]; onChange: (values: DraftComponent[]) => void; onNotice: (text: string) => void }) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const id = useId();
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const ready = search.trim().length >= 2 && debounced === search.trim();
  const query = useQuery({ queryKey: ["kombi-stock-search", debounced], queryFn: ready ? () => listStock({ search: debounced, limit: 12 }) : skipToken });
  return (
    <fieldset className="min-w-0 rounded-md border border-line p-3 space-y-2">
      <legend className="px-1 text-xs font-semibold text-ink">{slot === "Stückliste" ? "Komponenten" : `Slot ${slot}`}</legend>
      <Input aria-label={`Artikelsuche ${slot}`} placeholder="Sternnummer, DR-SKU oder Name" value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-sm" />
      <p className="text-[11px] text-ink-2">{search.trim().length < 2 ? "Ab 2 Zeichen suchen" : !ready || query.isLoading ? "Suche…" : "Treffer anklicken, um den Artikel aufzunehmen"}</p>
      {ready && query.error && <p role="alert" className="text-xs text-coral">Artikelsuche fehlgeschlagen — erneut suchen.</p>}
      {ready && query.data && <>
        <ul aria-label={`Suchtreffer ${slot}`} className="max-h-44 overflow-y-auto divide-y divide-line-2">
          {query.data.items.map(item => <li key={item.sku}>
            <button type="button" className="w-full text-left rounded-sm px-2 py-2 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={() => {
              if (components.some(c => c.sku === item.sku)) { onNotice(`${item.merchant_sku ?? item.sku} bereits enthalten — Menge direkt ändern`); return; }
              if (components.length >= (slot === "Stückliste" ? KOMBI_LIMITS.maxComponents : KOMBI_LIMITS.maxSlot)) { onNotice("max. 10 Artikel — weiterer Artikel nicht aufgenommen"); return; }
              onChange([...components, { sku: item.sku, merchant_sku: item.merchant_sku ?? null, name: item.name, qty: "1" }]); onNotice("");
            }}>
              <span className="font-mono text-xs font-semibold text-primary">{item.merchant_sku ?? item.sku}</span>{" "}
              {item.merchant_sku && <><span className="font-mono text-[11px] text-ink-2">{item.sku}</span>{" "}</>}
              <span className="text-xs text-ink">{item.name}</span>
            </button>
          </li>)}
        </ul>
        {query.data.items.length === 0 && <p className="text-xs text-ink-2">Keine Artikel gefunden</p>}
        {query.data.has_more && <p className="text-[11px] text-ink-2">Weitere Treffer vorhanden — Suche eingrenzen.</p>}
      </>}
      <ul aria-label={`Artikel ${slot}`} className="space-y-2">
        {components.map((c, index) => <li key={c.sku} className="flex items-start gap-2 border-t border-line-2 pt-2">
          <div className="min-w-0 flex-1"><GrundartikelLink code={c.merchant_sku ?? c.sku} className="font-mono text-xs text-primary" />{" "}<span className="font-mono text-[11px] text-ink-2">{c.merchant_sku ? c.sku : ""}</span><p className="text-xs text-ink truncate" title={c.name}>{c.name}</p></div>
          <div><Input aria-label={`Menge ${slot} ${c.sku}`} aria-invalid={!validQty(c.qty)} aria-describedby={!validQty(c.qty) ? `${id}-${index}` : undefined} inputMode="numeric" value={c.qty} className="h-8 w-16 font-mono" onChange={e => { onChange(components.map(v => v.sku === c.sku ? { ...v, qty: e.target.value } : v)); onNotice(""); }} />
            {!validQty(c.qty) && <p id={`${id}-${index}`} className="text-[11px] text-coral">Ganze Zahl 1–100</p>}</div>
          <Button size="icon" variant="ghost" className="size-8" aria-label={`Artikel ${slot} ${c.sku} entfernen`} onClick={() => onChange(components.filter(v => v.sku !== c.sku))}><X className="size-3" aria-hidden="true" /></Button>
        </li>)}
      </ul>
    </fieldset>
  );
}
